import React, { Suspense, useEffect, useLayoutEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Button from "../../shared/components/FormElements/Button";
import Modal from "../../shared/components/UIElements/Modal";
import Reveal from "../../shared/components/UIElements/Reveal";
import MaskReveal from "../../shared/components/UIElements/MaskReveal";
import SmartImage from "../../shared/components/UIElements/SmartImage";
import FeatureIcon from "../components/FeatureIcon";
import Preloader from "../components/Preloader";
import DemoRequestModal from "../components/DemoRequestModal";
import CapabilitiesShowcase from "../sections/CapabilitiesShowcase";
import ProjectManagementSection from "../sections/ProjectManagementSection";
import OperationsSection from "../sections/OperationsSection";
import CadenzaSection from "../sections/CadenzaSection";
import useSmoothScroll from "../../shared/hooks/useSmoothScroll";
import "./styling/HomePage.css";

gsap.registerPlugin(ScrollTrigger);

// The WebGL hero is code-split so three.js / r3f never block first paint.
const HeroCanvas = React.lazy(() => import("../hero/HeroCanvas"));

const prefersReducedMotion =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const FEATURE_CARDS = [
  {
    icon: "branch",
    title: "Scenario Branching",
    summary: "Run Baseline, Alternative, Optimization, or Custom scenarios side-by-side. Quantify the impact of operational decisions before committing to them.",
    details: [
      { heading: "Multiple Scenario Types", body: "Create Baseline, Alternative, Optimization, or fully Custom scenarios within the same project. Each scenario maintains its own ore properties, stacking plan, hydrodynamic configuration, and simulation results independently." },
      { heading: "Side-by-Side Comparison", body: "Run any combination of scenarios and compare their outputs on shared charts — daily recovery, cumulative recovery, PLS flows, and more — to directly quantify the benefit of one operational strategy over another." },
      { heading: "Non-Destructive Exploration", body: "Branch off a working baseline into an alternative without touching the original. Test aggressive irrigation schedules, different ore blends, or layout changes in isolation before committing to operational changes." },
    ],
  },
  {
    icon: "calibration",
    title: "Parameter Fitting & Calibration",
    summary: "Fit kinetic and hydrodynamic parameters against lab or field data. Ingest recorded flow rates, survey data, and plant performance to produce a history-matched baseline forecast.",
    details: [
      { heading: "Kinetic Parameter Fitting", body: "Upload column leach or laboratory test data and let Orecasting fit ultimate recovery, rate constant (k), and kinetic exponent (n) values for each ore-metal combination. Fitted parameters feed directly into the simulation without manual transcription." },
      { heading: "Hydrodynamic Calibration", body: "Match modelled moisture content and flow response against field-measured data by adjusting Brooks–Corey parameters. Iterative validation cycles reduce uncertainty in the unsaturated flow model before forward forecasting." },
      { heading: "History-Matched Baseline", body: "Ingest recorded PLS flow rates, plant feed grades, and survey moisture readings to anchor the model to real operational history. A calibrated baseline dramatically improves the reliability of scenario comparisons and forward projections." },
    ],
  },
  {
    icon: "link",
    title: "Coupled Flow + Reaction",
    summary: "Flow physics and kinetic reaction are solved together at every time step, capturing feedback effects that decoupled models miss.",
    details: [
      { heading: "Tight Coupling at Every Time Step", body: "At each simulated day, the 1D unsaturated flow solution and the kinetic extraction calculation share the same moisture and saturation state. Changes in irrigation, ore permeability, or heap geometry immediately affect both flow routing and recovery rates." },
      { heading: "Feedback Effects", body: "Low irrigation reduces in-heap moisture, slowing reaction contact time and decreasing daily recovery. Conversely, over-irrigation can saturate low-permeability layers, reducing effective flow to deeper ore. These non-linear feedbacks are captured automatically." },
      { heading: "Multi-Ore-Type Accuracy", body: "Each ore type in the heap column carries its own hydrodynamic and kinetic parameters. The coupled solver propagates solution through heterogeneous lifts correctly, rather than applying a single average behaviour to the entire heap." },
    ],
  },
  {
    icon: "building",
    title: "Multi-Tenant & Team-Ready",
    summary: "Companies, joint ventures, and fine-grained role-based access control — built for collaborative engineering teams and multi-site operations.",
    details: [
      { heading: "Company & Project Structure", body: "Organise work into Companies and Projects. Each company gets a unique organisation code for inviting team members. Projects can span multiple companies, supporting joint venture arrangements where separate organisations share access to the same simulation data." },
      { heading: "Role-Based Access Control", body: "Permissions are resolved per company and project context using a hybrid RBAC/ABAC model. Assign viewer, editor, or admin roles at the company or project level, with per-member overrides where needed. Admins bypass all scope checks." },
      { heading: "Concurrent Collaboration", body: "Multiple engineers can work within the same project simultaneously — configuring scenarios, reviewing results, and managing heap data — without conflicting with one another. Simulation runs execute in the background so the interface remains responsive for all users." },
    ],
  },
  {
    icon: "cloud",
    title: "Cloud-Native",
    summary: "Simulation jobs run asynchronously in the background. Results, artifacts, and snapshots are stored in cloud object storage and retrieved on demand.",
    details: [
      { heading: "Asynchronous Job Execution", body: "Submit a simulation run and continue working — configuring other scenarios, reviewing previous results, or managing project settings — while the job executes in the background. Status updates stream back to the UI in real time." },
      { heading: "Scalable Compute", body: "The simulation engine runs as a dedicated compute service, allowing resource-intensive runs to execute without impacting application responsiveness. Multiple runs across different projects can execute concurrently." },
      { heading: "Cloud Artifact Storage", body: "All simulation outputs — results files, heap snapshots, imported schedules, and calibration data — are stored in cloud object storage and retrieved via secure, time-limited links. No local file management is required." },
    ],
  },
  {
    icon: "factory",
    title: "Process Plant Integration",
    summary: "Route PLS flow rates and grades through a configurable process plant model to assess throughput constraints and downstream recovery efficiency within the same run.",
    details: [
      { heading: "PLS Routing", body: "Pregnant leach solution generated at the base of each heap is routed through the configured process plant model. The plant receives time-series flow rate and grade inputs from the simulation and returns processed solution back to the water circuit." },
      { heading: "Throughput Constraint Assessment", body: "Define plant capacity limits and processing efficiency parameters to identify periods where PLS production exceeds plant throughput. This allows operators to plan pond buffering, phased stacking, or irrigation rate adjustments to stay within operational limits." },
      { heading: "Integrated Water Circuit", body: "The process plant sits within Orecasting's node-based water balance, meaning barren solution returned from the plant feeds back into the irrigation circuit. This closed-loop representation ensures solution inventory and flow rates are consistent across the full simulation." },
    ],
  },
];

const RESULT_CARDS = [
  {
    src: "/images/home/Results Comparison - Daily Recoverry.png",
    alt: "Daily metal recovery comparison across scenarios",
    title: "Daily Metal Recovery",
    description: "Compare daily extracted metal across scenarios and ore types to identify recovery trends and underperforming periods.",
    featured: true,
  },
  {
    src: "/images/home/Results Comparison - PLS Flows.png",
    alt: "PLS flow rate comparison across scenarios",
    title: "PLS Flow Rates",
    description: "Track pregnant leach solution flow at the heap base over time — a key indicator of heap hydraulic performance and solution management.",
    featured: true,
  },
  {
    src: "/images/home/Results Comparison - Cumulative Recovery.png",
    alt: "Cumulative recovery curves",
    title: "Cumulative Recovery",
    description: "View total metal recovered over the operation lifetime with scenario-to-scenario comparison curves.",
    featured: false,
  },
  {
    src: "/images/home/Results Page - Main.png",
    alt: "Main results dashboard",
    title: "Results Dashboard",
    description: "Unified results overview displaying recovery metrics, flow data, and simulation run metadata in one place.",
    featured: false,
  },
  {
    src: "/images/home/Snapshots.png",
    alt: "Detailed heap snapshot view",
    title: "Heap Snapshots",
    description: "Inspect the full internal state of the heap at any simulated date — moisture content, saturation, per-block extraction progress and more — to guide targeted operational adjustments.",
    featured: false,
  },
];

const HomePage = () => {
  const [activeCard, setActiveCard] = useState(null);
  const [activeFeature, setActiveFeature] = useState(null);
  const [showDemo, setShowDemo] = useState(false);

  // Touch devices "swipe" rather than "scroll" — adjust the hero cue wording.
  const [isTouch, setIsTouch] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(pointer: coarse)");
    const onChange = (e) => setIsTouch(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // On hover-less (touch) devices, flip a Platform Features card when it scrolls
  // into the centre of the viewport — the touch equivalent of hover-to-flip.
  useEffect(() => {
    const noHover =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(hover: none)").matches;
    if (!noHover) return undefined;

    const cards = document.querySelectorAll(".feature-card--flip");
    if (!cards.length || typeof IntersectionObserver === "undefined") return undefined;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          e.target.classList.toggle("is-flipped", e.isIntersecting);
        });
      },
      // Thin band across the vertical centre of the viewport.
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    cards.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, []);

  // Scroll-driven entrances for the lower sections (desktop + motion allowed).
  // Cards rise/scale in as they scroll through; the About image parallaxes.
  // On mobile / reduced-motion this is skipped and the plain divs stay visible.
  useLayoutEffect(() => {
    if (prefersReducedMotion) return undefined;
    if (!window.matchMedia("(min-width: 901px)").matches) return undefined;

    const ctx = gsap.context(() => {
      gsap.from(".features-grid .feature-item", {
        scrollTrigger: {
          trigger: ".features-grid",
          start: "top 85%",
          end: "top 45%",
          scrub: 0.5,
        },
        y: 64,
        opacity: 0,
        stagger: 0.08,
      });

      gsap.from(".results-grid .result-item", {
        scrollTrigger: {
          trigger: ".results-grid",
          start: "top 85%",
          end: "top 42%",
          scrub: 0.5,
        },
        y: 72,
        opacity: 0,
        scale: 0.96,
        stagger: 0.08,
      });

      // Subtle vertical parallax on the (over-sized) About image.
      gsap.to(".about-image img", {
        scrollTrigger: {
          trigger: ".about-section",
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
        yPercent: -14,
        ease: "none",
      });

      gsap.from(".about-content > *", {
        scrollTrigger: {
          trigger: ".about-section",
          start: "top 80%",
          end: "top 45%",
          scrub: 0.5,
        },
        y: 44,
        opacity: 0,
        stagger: 0.06,
      });
    });

    // Recalculate once the pinned capabilities section has claimed its space.
    const refresh = () => ScrollTrigger.refresh();
    const t = setTimeout(refresh, 400);
    window.addEventListener("load", refresh);

    return () => {
      clearTimeout(t);
      window.removeEventListener("load", refresh);
      ctx.revert();
    };
  }, []);

  // Whether the WebGL hero is still visible (not yet fully covered by the
  // rising content). Toggled by the scroll handler to pause rendering when
  // covered — saves battery/GPU.
  const [heroActive, setHeroActive] = useState(true);
  // 1 at the top (full ripple/tilt) -> 0 as the user scrolls to explore, so the
  // hero pad settles back to its clean heap shape. A ref so it doesn't re-render.
  const heroInteractRef = useRef(1);

  // Weighted inertia scrolling for the marketing page (reduced-motion aware).
  useSmoothScroll();

  // Hide the native scrollbar for the full-bleed marketing page (Lenis still
  // drives scrolling via wheel/touch/keyboard). Scoped to while this page is
  // mounted so app screens keep their normal scrollbar.
  useEffect(() => {
    const html = document.documentElement;
    html.classList.add("home-no-scrollbar");
    return () => html.classList.remove("home-no-scrollbar");
  }, []);

  // Fade the hero copy (headline, tagline, CTAs) and scroll cue out as the page
  // scrolls, so the rising frosted content reads cleanly over just the graphic.
  // Driven directly against the DOM (no React re-render per scroll frame).
  useEffect(() => {
    const copy = document.querySelector(".hero-copy--center");
    const hint = document.querySelector(".hero-scroll-hint");
    const bodyEl = document.querySelector(".home-page__body");
    if (!copy) return undefined;

    let rafId = 0;
    const apply = () => {
      rafId = 0;
      const y = window.scrollY || window.pageYOffset || 0;
      const vh = window.innerHeight;

      // Hero copy fades OUT over the first ~55% of a screen.
      const progress = Math.min(1, y / (vh * 0.55));
      const op = 1 - progress;
      // Ease the hero pad back to its clean shape as the copy fades out.
      heroInteractRef.current = op;
      copy.style.opacity = String(op);
      copy.style.transform = `translateY(${progress * -48}px)`;
      // Don't let invisible CTAs stay clickable once faded out.
      copy.style.pointerEvents = op < 0.05 ? "none" : "";
      if (hint) hint.style.opacity = String(Math.max(0, 1 - progress * 2.2));

      // Frosted backdrop fades IN as the page scrolls up, so it "appears"
      // behind the cards (over ~90% of a screen of scroll).
      if (bodyEl) {
        const frost = Math.min(1, y / (vh * 0.9));
        bodyEl.style.setProperty("--frost-appear", String(frost));
      }

      // Pause the WebGL hero once it's fully covered by the risen content.
      const nextActive = y < vh * 0.98;
      setHeroActive((prev) => (prev === nextActive ? prev : nextActive));
    };
    const onScroll = () => {
      if (!rafId) rafId = requestAnimationFrame(apply);
    };

    const lenis = window.__lenis;
    if (lenis && lenis.on) lenis.on("scroll", onScroll);
    window.addEventListener("scroll", onScroll, { passive: true });
    apply();

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (lenis && lenis.off) lenis.off("scroll", onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // Retain the last opened content so the modals keep their contents while
  // they animate closed (CSSTransition keeps them mounted for 200ms after
  // the active state is cleared — otherwise they'd flash empty mid-close).
  const lastFeature = useRef(null);
  const lastCard = useRef(null);
  if (activeFeature) lastFeature.current = activeFeature;
  if (activeCard) lastCard.current = activeCard;
  const featureContent = activeFeature || lastFeature.current;
  const cardContent = activeCard || lastCard.current;

  return (
    <div className="home-page">

      <Preloader />

      <DemoRequestModal show={showDemo} onCancel={() => setShowDemo(false)} />

      {/* ── FEATURE DETAIL MODAL ──────────────────────────────────── */}
      <Modal
        show={!!activeFeature}
        onCancel={() => setActiveFeature(null)}
        header={featureContent ? `${featureContent.icon}  ${featureContent.title}` : ""}
        headerClass=""
        footerClass=""
        footer={<Button onClick={() => setActiveFeature(null)}>Close</Button>}
      >
        {featureContent && (
          <div className="feature-modal__body">
            {featureContent.details.map((section) => (
              <div key={section.heading} className="feature-modal__section">
                <h4 className="feature-modal__section-heading">{section.heading}</h4>
                <p className="feature-modal__section-body">{section.body}</p>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* ── IMAGE LIGHTBOX MODAL ──────────────────────────────────── */}
      <Modal
        show={!!activeCard}
        onCancel={() => setActiveCard(null)}
        header={cardContent?.title || ""}
        style={{ maxWidth: "min(1200px, 95vw)", width: "min(1200px, 95vw)" }}
        contentClass="result-lightbox__content"
        headerClass=""
        footerClass=""
        footer={<Button onClick={() => setActiveCard(null)}>Close</Button>}
      >
        {cardContent && (
          <div className="result-lightbox__body">
            <div className="result-lightbox__frame">
              <SmartImage
                src={cardContent.src}
                alt={cardContent.alt}
                className="result-lightbox__img"
                loading="eager"
              />
            </div>
            <div className="result-lightbox__info">
              <p className="result-lightbox__caption">{cardContent.description}</p>
            </div>
          </div>
        )}
      </Modal>

      {/* ── HERO ──────────────────────────────────────────────────── */}
      <section className="hero-cinematic">
        <div className="hero-canvas" aria-hidden="true">
          {prefersReducedMotion ? (
            <SmartImage
              src="/images/home/digitalheap.png"
              alt=""
              className="hero-canvas__fallback"
              loading="eager"
            />
          ) : (
            <Suspense fallback={null}>
              <HeroCanvas active={heroActive} interactRef={heroInteractRef} />
            </Suspense>
          )}
        </div>
        <div className="hero-overlay" aria-hidden="true" />

        <div className="hero-copy hero-copy--center">
          <span className="hero-brand">
            <span className="hero-brand__mark" aria-hidden="true" />
            <span className="hero-eyebrow">Orecasting</span>
          </span>
          <h1 className="hero-title">Heap Leach Intelligence, Simulated.</h1>
          <p className="hero-tagline">
            A unified simulation platform for precision recovery forecasting,
            operational optimization, and confident decision making.
          </p>
          <ul className="hero-bullets">
            <li>Physics-based 1D flow + kinetic coupling</li>
            <li>Scenario branching &amp; comparative analytics</li>
            <li>Automated parameter fitting &amp; validation</li>
            <li>Detailed heap snapshots for operational insight</li>
          </ul>
          <div className="hero-cta-row">
            <Button to="/auth" className="primary-cta">Sign In</Button>
            <Button inverse className="secondary-cta" onClick={() => setShowDemo(true)}>Request Demo</Button>
          </div>
        </div>

        <div className="hero-scroll-hint" aria-hidden="true">
          {isTouch ? (
            <span className="hero-scroll-hint__swipe">
              <span className="hero-scroll-hint__chevron" />
              <span className="hero-scroll-hint__chevron" />
            </span>
          ) : (
            <span className="hero-scroll-hint__track">
              <span className="hero-scroll-hint__dot" />
            </span>
          )}
          {isTouch ? "Swipe to explore" : "Scroll to explore"}
        </div>
      </section>

      {/* Constrained column for the rest of the page (SEO + feature content) */}
      <div className="home-page__body">

      {/* ── CORE CAPABILITIES (pinned cross-fade scroll-telling) ────── */}
      <CapabilitiesShowcase />

      {/* ── PLATFORM FEATURES ─────────────────────────────────────── */}
      <section className="features-section" aria-labelledby="features-heading">
        <MaskReveal as="h2" id="features-heading" className="section-heading">Platform Features</MaskReveal>
        <div className="features-grid">
          {FEATURE_CARDS.map((feature) => (
            <div key={feature.title} className="feature-item">
              <button
                className="feature-card feature-card--flip"
                onClick={() => setActiveFeature(feature)}
                aria-label={`${feature.title} — hover for a summary, click for details`}
              >
                <span className="feature-flip">
                  <span className="feature-flip__face feature-flip__front">
                    <span className="feature-icon" aria-hidden="true">
                      <FeatureIcon name={feature.icon} />
                    </span>
                    <h3>{feature.title}</h3>
                  </span>
                  <span className="feature-flip__face feature-flip__back">
                    <p>{feature.summary}</p>
                    <span className="feature-card__more">Learn more →</span>
                  </span>
                </span>
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ── SIMULATION RESULTS ────────────────────────────────────── */}
      <section className="results-section" aria-labelledby="results-heading">
        <MaskReveal as="h2" id="results-heading" className="section-heading">Simulation Results</MaskReveal>
        <Reveal as="p" className="results-intro">
          Orecasting produces a comprehensive suite of time-series outputs for
          every simulation run, giving operators and engineers the data they
          need to evaluate performance and plan ahead.
        </Reveal>
        <div className="results-grid">
          {RESULT_CARDS.map((card) => (
            <div
              key={card.title}
              className={`result-item${card.featured ? " result-item--featured" : ""}`}
            >
              <button
                className="result-card"
                onClick={() => setActiveCard(card)}
                aria-label={`View full size: ${card.title}`}
              >
                <SmartImage src={card.src} alt={card.alt} />
                <div className="result-card-label">
                  <h4>{card.title}</h4>
                  <p>{card.description}</p>
                </div>
                <span className="result-card-zoom" aria-hidden="true">⤢</span>
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ── PROJECT MANAGEMENT ────────────────────────────────────── */}
      <ProjectManagementSection />

      {/* ── OPERATIONS: DATA + MAINTENANCE LOGGING ────────────────── */}
      <OperationsSection />

      {/* ── CADENZA (MCP / AI INTEGRATION) ────────────────────────── */}
      {/* Placed last of the feature sections: it reaches across everything
          above it, so it only lands once that data has been introduced. */}
      <CadenzaSection />

      {/* ── ABOUT / SEO ───────────────────────────────────────────── */}
      <section className="about-section" aria-labelledby="about-heading">
        <MaskReveal as="h2" id="about-heading" className="section-heading">Built for Heap Leach Engineering</MaskReveal>
        <div className="about-layout">
          <div className="about-image">
            <SmartImage
              src="/images/home/Excavator And Haul Truck.jpg"
              alt="Excavator and haul truck on an open-pit mine site — heap leach mining operations"
            />
          </div>
          <div className="about-content">
            <p>
              Orecasting is purpose-built heap leach simulation software for mineral processing
              engineers. Unlike generic spreadsheet models or decoupled flow tools, Orecasting
              solves unsaturated flow physics and kinetic extraction simultaneously at every
              simulated time step — capturing the non-linear feedback effects that drive real
              heap leach performance: irrigation response, moisture retention, permeability
              changes across ore lifts, and their direct impact on daily metal recovery.
            </p>
            <p>
              The platform supports the full heap leach engineering workflow: import mine
              schedules and stacking plans, fit kinetic parameters against laboratory column
              leach or field data, configure Brooks&#8211;Corey hydrodynamics per ore type,
              model the site water balance with climate and pond inventory, then run forward
              scenarios to forecast daily metal recovery, PLS grades, and cumulative extraction
              for gold, silver, copper, and other heap-leachable commodities.
            </p>
            <p>
              Developed by Forte Dynamics, Orecasting is a cloud-native mining simulation
              platform — simulation jobs execute asynchronously, results are stored securely
              in cloud object storage, and engineering teams collaborate across projects with
              fine-grained role-based access control. It is the only ore forecasting and heap
              leach simulation platform designed specifically for operational decision-making
              at active mine sites.
            </p>
            <ul className="about-keywords">
              <li>Heap Leach Simulation</li>
              <li>Ore Forecasting</li>
              <li>Leach Pad Modelling</li>
              <li>Mineral Processing Software</li>
              <li>Kinetics Parameter Fitting</li>
              <li>1D Unsaturated Flow</li>
              <li>Mining Simulation Software</li>
              <li>PLS Flow Modelling</li>
              <li>Recovery Forecasting</li>
              <li>Heap Leach Optimisation</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ── CONTACT ───────────────────────────────────────────────── */}
      <div className="contact-info">
        <MaskReveal as="h2">Contact Us</MaskReveal>
        <Reveal className="contact-grid">
          {/* Office addresses intentionally not listed — post-acquisition there
              are too many to enumerate, and the old ones were stale. */}
          <div className="contact-details">
            <p>
              <a href="tel:+17206429359" className="contact-link">(720) 642-9359</a>
              <br />
              <a
                href="mailto:service@fortedynamics.com?subject=Orecasting%20Enquiry"
                className="contact-link"
              >
                service@fortedynamics.com
              </a>
            </p>
          </div>
        </Reveal>
      </div>

      </div>{/* .home-page__body */}

    </div>
  );
};

export default HomePage;
